import {Dictionary} from 'lodash';
import React, {FC, useMemo} from 'react';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {FrontEvent, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {DeprecatedSearchDropdown} from '../../../../components/common/dropdowns/search/deprecatedSearchDropdown';
import {SyncDropdownSource} from '../../../../components/common/dropdowns/source/syncDropdownSource';
import {useAppSelector} from '../../../../helpers/reduxHelpers';
import {AccountModel} from '../../../../models/api/accountModel';
import {AnalyticsFilterTypesEnum} from '../../../../models/api/analyticsSavedViewModel';
import {ChannelModel} from '../../../../models/api/channelModel';
import {InboxModel} from '../../../../models/api/inboxModel';
import {TagModel} from '../../../../models/api/tagModel';
import {TeammateModel} from '../../../../models/api/teammateModel';
import {TeamModel} from '../../../../models/api/teamModel';
import {ResourceMap} from '../../../../store/appTypes';
import {getChannels} from '../../../../store/data/channels/channelsSelectors';
import {getTeammates} from '../../../../store/data/teammates/teammatesSelectors';
import {getTeams} from '../../../../store/data/teams/teamsSelectors';
import {
  getAnalyticsViewsAllAccounts,
  getAnalyticsViewsAllInboxes,
  getAnalyticsViewsAllTagNames,
  getAnalyticsViewsAllTags,
} from '../../../../store/panels/analytics/views/analyticsPanelViewsSelectors';
import {
  AnalyticsSavedView,
  AnalyticsSelectedViewId,
  isAnalyticsViewTemporary,
} from '../../../../store/panels/analytics/views/analyticsPanelViewsState';
import {makeFilterRows} from '../../../helpers/analyticsFiltersHelpers';
import {TabDropdownElement} from '../../tabs/tabDropdownElement';
import {SavedViewsDropdownItem} from './reportHeaderSavedViewsDropdownItem';

/*
 * Props.
 */

interface SavedViewsDropdownProps {
  savedViews: ReadonlyArray<AnalyticsSavedView>;
  onSelect: HandlerOf<AnalyticsSelectedViewId>;
}

/*
 * Types.
 */

export interface AnalyticsResourceMap {
  [AnalyticsFilterTypesEnum.ACCOUNTS]: ResourceMap<AccountModel>;
  [AnalyticsFilterTypesEnum.CHANNELS]: ResourceMap<ChannelModel>;
  [AnalyticsFilterTypesEnum.INBOXES]: Dictionary<InboxModel>;
  [AnalyticsFilterTypesEnum.TAGS]: Dictionary<string>;
  [AnalyticsFilterTypesEnum.EXCLUDE_TAGS]: Dictionary<string>;
  [AnalyticsFilterTypesEnum.FULL_TAGS]: Dictionary<TagModel>;
  [AnalyticsFilterTypesEnum.TEAMMATES]: ResourceMap<TeammateModel>;
  [AnalyticsFilterTypesEnum.TEAMS]: ResourceMap<TeamModel>;
}

/*
 * Component.
 */

export const SavedViewsDropdown: FC<SavedViewsDropdownProps> = ({savedViews, onSelect}) => {
  // We need to retrieve resource maps.
  const allAccounts = useAppSelector(getAnalyticsViewsAllAccounts);
  const allChannels = useAppSelector(getChannels);
  const allInboxes = useAppSelector(getAnalyticsViewsAllInboxes);
  const allTeammates = useAppSelector(getTeammates);
  const allTeams = useAppSelector(getTeams);
  const allTagNames = useAppSelector(getAnalyticsViewsAllTagNames);
  const allTags = useAppSelector(getAnalyticsViewsAllTags);

  const resourceMaps = useMemo(
    () => ({
      [AnalyticsFilterTypesEnum.ACCOUNTS]: allAccounts,
      [AnalyticsFilterTypesEnum.CHANNELS]: allChannels,
      [AnalyticsFilterTypesEnum.INBOXES]: allInboxes,
      [AnalyticsFilterTypesEnum.TAGS]: allTagNames,
      [AnalyticsFilterTypesEnum.EXCLUDE_TAGS]: allTagNames,
      [AnalyticsFilterTypesEnum.FULL_TAGS]: allTags,
      [AnalyticsFilterTypesEnum.TEAMMATES]: allTeammates,
      [AnalyticsFilterTypesEnum.TEAMS]: allTeams,
    }),
    [allAccounts, allChannels, allInboxes, allTagNames, allTags, allTeammates],
  );

  const renderRow = (item: AnalyticsSavedView) => {
    const onItemSelect = (event: FrontEvent) => {
      event.preventDefault(); // Keep the dropdown open

      // If item is pinned, then we cannot set or unset it.
      if (item.isPinned) {
        return;
      }

      onSelect(item.id);
    };

    return <SavedViewsDropdownItem item={item} resourceMaps={resourceMaps} onSelect={onItemSelect} />;
  };

  const dropdownSource = new SyncDropdownSource({
    items: [...savedViews.filter((view) => !isAnalyticsViewTemporary(view.id))],
    rowFilter: filterRows,
    renderRow,
    rowHeightPredicate: () => 56,
  });

  if (savedViews.length === 0) {
    return null;
  }

  return (
    <TabDropdownElement
      label={<Trans id="tabs-all-view">All views</Trans>}
      renderDropdown={(popoverProps: PopoverTransferProps) => (
        <DeprecatedSearchDropdown
          {...popoverProps}
          width={300}
          placement="top-end"
          title={<Trans id="analytics-all-views-dropdown-title">All Views</Trans>}
          source={dropdownSource}
        />
      )}
    />
  );
};

/*
 * Helpers.
 */

const filterRows = makeFilterRows<AnalyticsSavedView>((item) => item.name);
